import * as Actions from '@actions/index';
import { AccountService } from '@services/index';
import { AppState, selectDataFromCommonEntity } from '@reducers/index';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DashletLineGraphComponent } from '../dashlet-line-graph/dashlet-line-graph.component';
import { DashletTotalPortsByType } from '@models/index';
import { select, Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-dashlet-total-ports-by-type',
    templateUrl: './dashlet-total-ports-by-type.component.html',
    styleUrls: ['./dashlet-total-ports-by-type.component.scss']
})
export class DashletTotalPortsByTypeComponent implements OnInit, OnDestroy {
    @Input() dashlet: DashletTotalPortsByType;
    private destroy$ = new Subject<void>();
    entityGroup: number;
    customerId: string;

    private pportsChart: DashletLineGraphComponent;
    get portsChart() {
        return this.pportsChart;
    }
    @ViewChild('portsChart') set portsChart(value: DashletLineGraphComponent) {
        this.pportsChart = value;
        this.updateChart();
    }

    private pportsChartWide: DashletLineGraphComponent;
    get portsChartWide() {
        return this.pportsChartWide;
    }
    @ViewChild('portsChartWide') set portsChartWide(value: DashletLineGraphComponent) {
        this.pportsChartWide = value;
        this.updateChart();
    }

    lineData = {};

    private totalPortsBillable = {
        label: 'Total Billable Ports',
        backgroundColor: '#3399ff',
        borderColor: '#3399ff',
        pointBackgroundColor: 'white',
        pointHighlightStroke: 'white',
        pointRadius: 0,
        pointHitRadius: 10,
        fill: true,
        data: []
    };
    private totalPortsNonBillable = {
        label: 'Total Non-Billable Ports',
        backgroundColor: '#336699',
        borderColor: '#336699',
        pointBackgroundColor: 'white',
        pointHighlightStroke: 'white',
        pointRadius: 0,
        pointHitRadius: 10,
        fill: true,
        data: []
    };

    portsLineData: any = {};
    portsChartOptions: any = {
        scales: {
            x: {
                type: 'time',
                grid: {
                    display: false
                },
                time: {
                    unit: 'day',
                    displayFormats: {
                        day: 'd MMM'
                    },
                    stepSize: 3,
                    max: null,
                    min: null
                },
                ticks: {
                    fontSize: 10.2,
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0
                }
            },
            y: {
                stacked: true,
                grid: {
                    display: false
                },
                ticks: {
                    fontSize: 10.2,
                    beginAtZero: true
                }
            }
        },
        plugins: {
            legend: {
                display: false,
                position: 'bottom',
                reverse: true
            },
            tooltip: {
                callback: {
                    title: tipItem => {
                        return null;
                    }
                }
            }
        },

        animation: {
            duration: 0
        }
    };
    portsChartOptionsWide: any = {
        scales: {
            x: {
                type: 'time',
                grid: {
                    display: false
                },
                time: {
                    unit: 'day',
                    displayFormats: {
                        day: 'd MMM'
                    },
                    stepSize: 3,
                    max: null,
                    min: null
                },
                ticks: {
                    fontSize: 10.2,
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0
                }
            },
            y: {
                stacked: true,
                grid: {
                    display: false
                },
                ticks: {
                    fontSize: 10.2,
                    beginAtZero: true
                }
            }
        },
        plugins: {
            legend: {
                display: false,
                position: 'bottom',
                reverse: true
            },
            tooltip: {
                callback: {
                    title: tipItem => {
                        return null;
                    }
                }
            }
        },

        animation: {
            duration: 0
        }
    };

    constructor(private accountService: AccountService, private store$: Store<AppState>) {
        this.portsLineData = {
            datasets: [this.totalPortsNonBillable, this.totalPortsBillable]
        };
    }

    ngOnInit() {
        this.dashlet.onChartDataUpdated.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.updateChart();
        });
        var userDetail = this.accountService.getUserDetails();
        this.entityGroup = userDetail.EntityGroup;
        // network data

        this.store$.dispatch(
            Actions.SubscribeToRealTimeService({
                equipmentId: this.dashlet._equipmentId,
                command: this.dashlet._commandTypeIdPort
            })
        );
        this.store$.dispatch(
            Actions.GetNotifyCommonEntitys({
                equipmentId: this.dashlet._equipmentId,
                commandTypeId: this.dashlet._commandTypeIdPort
            })
        );

        this.store$
            .pipe(
                select(selectDataFromCommonEntity(this.dashlet._equipmentId + this.dashlet._commandTypeIdPort)),
                takeUntil(this.destroy$)
            )
            .subscribe(data => {
                if (data) {
                    this.dashlet.processPortData(data);
                }
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.dashlet) this.dashlet.dispose();
    }

    private updateChart() {
        this.totalPortsBillable.data = this.dashlet.portsPerTypeArrayBillable;
        this.totalPortsNonBillable.data = this.dashlet.portsPerTypeArrayNonBillable;

        //update axis range
        let max = new Date();
        max.setDate(max.getDate() - 1);

        let min = new Date(max);
        min.setDate(min.getDate() - 15); //then show up to 15 days before max
        this.portsChartOptions.scales.x.min = min;
        this.portsChartOptions.scales.x.max = max;

        min = new Date(max);
        min.setDate(min.getDate() - 35); // show up to 35 days before max
        this.portsChartOptionsWide.scales.x.min = min;
        this.portsChartOptionsWide.scales.x.max = max;
        if (this.portsChart) {
            this.portsChart.updateChart();
        }
        if (this.portsChartWide) {
            this.portsChartWide.updateChart();
        }
    }

    get smallLayout() {
        return this.dashlet.getSize().id === 0;
    }

    get mediumLayout() {
        return this.dashlet.getSize().id === 1;
    }

    get wideLayout() {
        return this.dashlet.getSize().id === 2;
    }
}
